// @ts-check

/** @typedef { import('js-cookie').CookieAttributes } CookieAttributes */

/**
 * This module is only intended for use in a client-side web-browser execution context;
 * however it is incidentally required on Node during testing and server-side rendering.
 * This conditional is to prevent `require('client/lib/cookie')` from failing;
 * the exports of this module should never be called from outside of the browser.
 */
const isSecure = typeof window === 'object'
? window.location.protocol === 'https:'
: false

/**
 * A centralized lookup table of cookie names to attributes
 * @type {Object.<string, CookieAttributes>}
 */
const attributesByName = {
  'device_override': attributesWithDefaults(),
  'ftu_tooltips': attributesWithDefaults(),
  'link-modal-dismissed': attributesWithDefaults({
    expires: 1 // day
  }),
  'mix-ext': attributesWithDefaults(),
  'open-in-app-dismissed': attributesWithDefaults({
    expires: 7 // days
  }),
  'temp_id': attributesWithDefaults(),
  'user_identified': attributesWithDefaults(),
  'usr_sess': attributesWithDefaults()
}

/**
 * @param {CookieAttributes} [attrs]
 * @returns {CookieAttributes}
 */
function attributesWithDefaults(attrs) {
  return {
    sameSite: 'lax',
    secure: isSecure,
    ...attrs
  }
}

/**
 * Returns `Cookies.remove` options for a given cookie name
 * @param {string} cookieName
 * @returns {CookieAttributes}
 */
function clearAttributes(cookieName) {
  const result = setAttributes(cookieName)
  delete result.expires
  return result
}

/**
 * Returns `Cookies.set` options for a given cookie name
 * @param {string} cookieName
 * @returns {CookieAttributes}
 */
function setAttributes(cookieName) {
  if (cookieName in attributesByName) {
    return { ...attributesByName[cookieName] }
  }
  throw new Error('Unrecognized cookie name: ' + cookieName)
}

module.exports = {
  clearAttributes,
  setAttributes
}
